import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { UserInfoEngine } from '@memberjunction/core-entities';
import { AGURenderService } from 'agu_app_shell';
import { ResearchMemberCv } from '../../models/research.models';
import { PdfHighlight } from './cx-pdf-viewer.component';
import {
    ReviewCard, ReviewCategories, ReviewCategory, ReviewCategoryLabels, ReviewCategoryNouns,
} from './career-explorer.state';

/** The dismissed-note setting (`MJ: User Settings` through `UserInfoEngine`, the OEP tour / Home streak pattern). */
export const REVIEW_NOTE_SETTING = 'agu.hub.cx.reviewNote.v1';
const REVIEW_NOTE_DISMISSED = 'dismissed';

/** The width at and below which the review drops the CV viewer (P6:672, 705). */
export const PHONE_MAX_WIDTH_PX = 760;

/** Phone tap-target minimum (P6:643). */
export const PHONE_TARGET_PX = 44;

const IsTypingTarget = (target: EventTarget | null): boolean => {
    const el = target as { tagName?: string; isContentEditable?: boolean } | null;
    return /^(input|textarea|select)$/i.test(el?.tagName ?? '') || el?.isContentEditable === true;
};

/**
 * The Career Explorer review view (P6:556-575 tabs, note and split; :1709-1732 the card; :1833-1839 the keys): each tag is checked
 * against the member's real CV, with the evidence highlighted, and accepted or declined with the mouse or A / D. Presentational: the
 * page owns the data and sends the decisions.
 */
@Component({
    standalone: false,
    selector: 'ra-cx-review',
    templateUrl: './cx-review.component.html',
    styleUrl: './cx-review.component.scss',
})
export class CxReviewComponent implements OnInit, OnDestroy {
    @Input() Cards: readonly ReviewCard[] = [];
    @Input() Category: ReviewCategory = 'skill';
    /** Distinct tags the CV states outright (the static first tab). */
    @Input() StatedCount = 0;
    @Input() PendingByCategory: Readonly<Record<ReviewCategory, number>> = { skill: 0, education: 0, license: 0, certification: 0, title: 0 };
    @Input() Cv: ResearchMemberCv | null = null;
    @Input() DownloadUrl: string | null = null;

    /** One decision for one card: the card's id only. */
    @Output() readonly Accept = new EventEmitter<string[]>();
    /** One decision per concept: the card's id and every chain member it stands for (JG section 4). */
    @Output() readonly Decline = new EventEmitter<string[]>();
    @Output() readonly AcceptAll = new EventEmitter<void>();
    @Output() readonly Next = new EventEmitter<ReviewCategory>();
    @Output() readonly DismissNote = new EventEmitter<void>();
    /** The done card's "See your career paths" when nothing is left to review. */
    @Output() readonly SeePaths = new EventEmitter<void>();
    /** The PDF could not be loaded: the page may refetch the link once. */
    @Output() readonly ViewerFailed = new EventEmitter<void>();

    readonly Categories = ReviewCategories;
    NoteOpen = true;
    /** At phone width the viewer is not rendered at all (spec F8; P6:705), so pdf.js and the PDF are never fetched there. */
    ShowViewer = true;
    private pickedID: string | null = null;
    private phoneQuery: MediaQueryList | null = null;
    private memo: { id: string | null; cv: ResearchMemberCv | null; value: PdfHighlight[] } | null = null;
    private readonly onPhoneChange = (): void => this.SyncViewer();

    constructor(@Optional() private readonly render: AGURenderService | null = null) {}

    ngOnInit(): void {
        if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
            this.phoneQuery = window.matchMedia(`(max-width: ${PHONE_MAX_WIDTH_PX}px)`);
            this.phoneQuery.addEventListener('change', this.onPhoneChange);
            this.SyncViewer();
        }
        void this.ReadNote();
    }

    ngOnDestroy(): void {
        this.phoneQuery?.removeEventListener('change', this.onPhoneChange);
    }

    private SyncViewer(): void {
        const show = !(this.phoneQuery?.matches ?? false);
        if (show === this.ShowViewer) return;
        this.ShowViewer = show;
        this.render?.Notify();
    }

    /** Fail-silent: when the setting cannot be read the note simply shows (P6:1706). */
    private async ReadNote(): Promise<void> {
        try {
            await UserInfoEngine.Instance.Config(false);
            if (UserInfoEngine.Instance.GetSetting(REVIEW_NOTE_SETTING) === REVIEW_NOTE_DISMISSED) {
                this.NoteOpen = false;
                this.render?.Notify();
            }
        } catch {
            // the note stays open
        }
    }

    // ── Cards ────────────────────────────────────────────────────────────

    get CategoryCards(): ReviewCard[] { return this.Cards.filter((c) => c.category === this.Category); }
    get Pending(): ReviewCard[] { return this.CategoryCards.filter((c) => c.reviewStatus === 'unreviewed'); }

    /** The card under review: the one the member picked from NEXT UP, else the first pending. Null when the category is done. */
    get Current(): ReviewCard | null {
        const pending = this.Pending;
        return pending.find((c) => c.providerTagID === this.pickedID) ?? pending[0] ?? null;
    }

    get DecidedCount(): number { return this.CategoryCards.length - this.Pending.length; }
    get ProgressText(): string { return `${this.DecidedCount + 1} OF ${this.CategoryCards.length}`; }
    get ProgressPercent(): number {
        const total = this.CategoryCards.length;
        return total === 0 ? 0 : Math.round((100 * this.DecidedCount) / total);
    }

    /** The category chip, then `implied` or "read with less certainty"; never a number (deviation from P6:1720's "high/medium/low confidence"). */
    ChipsOf(card: ReviewCard): string[] {
        const kind = card.confidence === 'plausible' ? 'read with less certainty' : 'implied';
        return [this.NounOf(card.category), kind];
    }

    NounOf(category: string): string {
        return (ReviewCategories as readonly string[]).includes(category) ? ReviewCategoryNouns[category as ReviewCategory] : 'Other';
    }

    ConfidenceLabel(card: ReviewCard): string { return card.confidence === 'plausible' ? 'less certain' : 'implied'; }

    /**
     * The boxes for the card under review. The same array comes back until the card or the CV changes, so the viewer's
     * `[Highlight]` binding is stable across change-detection passes (a fresh array each pass re-placed and re-scrolled forever).
     */
    get CurrentHighlights(): PdfHighlight[] {
        const id = this.Current?.providerTagID ?? null;
        if (this.memo === null || this.memo.id !== id || this.memo.cv !== this.Cv) {
            this.memo = { id, cv: this.Cv, value: this.HighlightsOf(this.Current) };
        }
        return this.memo.value;
    }

    /**
     * The evidence boxes for the card: every location of each segment the card QUOTES, in quote order, so the first box (the one
     * the viewer scrolls to) is the first quote. Built from `card.quotes` rather than the tag's rows: those include `stated` rows
     * the quotes skip (`GroupReviewTags`), which put the first box on a line the card does not show (H-14 live).
     */
    HighlightsOf(card: ReviewCard | null): PdfHighlight[] {
        if (!card || !this.Cv) return [];
        const byRef = new Map(this.Cv.segments.map((s) => [s.segmentRef, s] as const));
        const out: PdfHighlight[] = [];
        for (const quote of card.quotes) {
            for (const loc of byRef.get(quote.segmentRef)?.locations ?? []) {
                if (loc.bbox.length === 4) out.push({ page: loc.page, bbox: [loc.bbox[0], loc.bbox[1], loc.bbox[2], loc.bbox[3]] });
            }
        }
        return out;
    }

    // ── Tabs ─────────────────────────────────────────────────────────────

    LabelOf(category: ReviewCategory): string { return ReviewCategoryNouns[category]; }
    TabCount(category: ReviewCategory): string {
        const n = this.PendingByCategory[category];
        return n > 0 ? `${n} pending` : '✓ done';
    }
    IsDone(category: ReviewCategory): boolean { return this.PendingByCategory[category] === 0; }

    OpenCategory(category: ReviewCategory): void { this.Next.emit(category); }

    // ── Actions ──────────────────────────────────────────────────────────

    Pick(id: string): void { this.pickedID = id; }

    OnAccept(): void {
        const card = this.Current;
        if (!card) return;
        this.pickedID = null;
        this.Accept.emit([card.providerTagID]);
    }

    OnDecline(): void {
        const card = this.Current;
        if (!card) return;
        this.pickedID = null;
        this.Decline.emit([card.providerTagID, ...card.chainMembers]);
    }

    OnAcceptAll(): void {
        this.pickedID = null;
        this.AcceptAll.emit();
    }

    /** P6:1833-1839: A / D, ignored with a modifier or while typing in a field. */
    @HostListener('document:keydown', ['$event'])
    OnKeydown(event: KeyboardEvent): void {
        // A held key repeats: one press is one decision.
        if (event.repeat || event.metaKey || event.ctrlKey || event.altKey || IsTypingTarget(event.target)) return;
        const key = event.key.toLowerCase();
        if (key === 'a') this.OnAccept();
        else if (key === 'd') this.OnDecline();
    }

    DismissInfo(): void {
        this.NoteOpen = false;
        try {
            UserInfoEngine.Instance.SetSettingDebounced(REVIEW_NOTE_SETTING, REVIEW_NOTE_DISMISSED);
        } catch {
            // not persisted: the note shows again next visit
        }
        this.DismissNote.emit();
    }

    // ── Done card ────────────────────────────────────────────────────────

    /** The first category with tags still pending. */
    get NextCategory(): ReviewCategory | null {
        return ReviewCategories.find((c) => this.PendingByCategory[c] > 0) ?? null;
    }

    get DoneTitle(): string { return `${ReviewCategoryLabels[this.Category]} reviewed`; }

    get DoneBody(): string {
        const next = this.NextCategory;
        return next === null
            ? 'Everything is reviewed. Your scores are as sharp as they get.'
            : `Next: ${this.PendingByCategory[next]} ${ReviewCategoryLabels[next].toLowerCase()} to check.`;
    }

    get DoneButton(): string {
        const next = this.NextCategory;
        return next === null ? 'See your career paths →' : `Review ${ReviewCategoryLabels[next].toLowerCase()} →`;
    }

    OpenNext(): void {
        const next = this.NextCategory;
        if (next === null) this.SeePaths.emit();
        else this.Next.emit(next);
    }
}
